import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor as LibPointerSensor,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import {
  stepTypesLabels,
  defaultCompoundStepCount,
  defaultCompoundStepCountOperator,
} from '../../../constants/compound';
import { isStepEnabled } from '../../../utils/compound';

const SortableListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;

  .sortable-list-wrapper {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }

  .button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 20px 20px;
    margin: 1px 1px;
    border: 1px dotted #999999;
  }
`;

// Функция для отмены перетаскивания при клике по элементам ввода, кнопкам, иконкам
const shouldHandleEvent = ({ nativeEvent: event }) => {
  const targetElement = event.target;
  const targetElementClasses = targetElement.className;
  const isSVG = !!targetElement.closest('svg');
  const isButton = !!targetElement.closest('button');
  const bannedClasses = ['rc-virtual-list-holder', 'ant'];

  return (
    !isButton &&
    !isSVG &&
    !bannedClasses.some((disabledClass) => targetElementClasses.includes(disabledClass))
  );
};

// нужен для того, чтобы обрабатывать MouseEvents при D&D
class PointerSensor extends LibPointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: shouldHandleEvent,
    },
  ];
}

const prepareStepsData = (steps) => {
  const firstEnabledStepIndex = steps.findIndex((step) => isStepEnabled(step.enabled));
  const getLabel = (index) =>
    index <= firstEnabledStepIndex ? stepTypesLabels.FIRST : stepTypesLabels.POSITIVE;

  return (steps || []).map((step, index) => ({
    label: getLabel(index),
    event: step.event,
    closingRange: step.closingRange,
    stepType: step.type,
    filter: step.filter,
    firstEnabledStepIndex,
    enabled: step.enabled,
    optional: step.optional,
    count: step.count || defaultCompoundStepCount,
    countOperator: step.countOperator || defaultCompoundStepCountOperator,
    stepIndex: index,
    key: index,
    id: step.id,
  }));
};

const CompoundUpdateStepContainer = ({
  onAddStep,
  onChangeStepPosition,
  steps,
  renderUpdateStep,
}) => {
  const preparedSteps = useMemo(() => prepareStepsData(steps), [steps]);

  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        onChangeStepPosition(active.data.current.sortable.index, over.data.current.sortable.index);
      }
    },
    [onChangeStepPosition]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return (
    <SortableListContainer>
      <div className="sortable-list-wrapper">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={preparedSteps} strategy={verticalListSortingStrategy}>
            {preparedSteps.map((item) => (
              <React.Fragment key={item.id}>{renderUpdateStep(item)}</React.Fragment>
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeId ? renderUpdateStep(preparedSteps.find(({ id }) => id === activeId)) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <div className="button-container">
        <Button onClick={onAddStep}>
          <PlusOutlined />
        </Button>
      </div>
    </SortableListContainer>
  );
};

export default CompoundUpdateStepContainer;
